import React from 'react';
import { useLanguage, SUPPORTED_LANGUAGES } from '../../contexts/LanguageContext';
import { Button } from '../ui/button';
import { Globe, Check } from 'lucide-react';
import { motion } from 'framer-motion';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';

const translations = {
  system: { 'pt-BR': 'Sistema', 'en-US': 'System' },
  language: { 'pt-BR': 'Idioma', 'en-US': 'Language' },
};

export const LanguageToggle = ({ className = '', variant = 'icon' }) => {
  const { language, languagePreference, setLanguage, t } = useLanguage();
  
  const getCurrentFlag = () => {
    if (languagePreference === 'system') {
      return '🌐';
    }
    return SUPPORTED_LANGUAGES[language]?.flag || '🌐';
  };

  // Menu variant - renders as inline buttons for use inside dropdown menus
  if (variant === 'menu') {
    return (
      <div className="flex flex-wrap gap-2">
        <Button
          variant={languagePreference === 'system' ? 'default' : 'outline'}
          size="sm"
          onClick={() => setLanguage('system')}
          className={`rounded-full text-xs ${languagePreference === 'system' ? 'bg-orange-500 hover:bg-orange-600' : ''}`}
          data-testid="language-system-menu"
        >
          <Globe className="w-3 h-3 mr-1" />
          {t(translations.system)}
        </Button>
        {Object.values(SUPPORTED_LANGUAGES).map((lang) => (
          <Button
            key={lang.code}
            variant={languagePreference === lang.code ? 'default' : 'outline'}
            size="sm"
            onClick={() => setLanguage(lang.code)}
            className={`rounded-full text-xs ${languagePreference === lang.code ? 'bg-orange-500 hover:bg-orange-600' : ''}`}
            data-testid={`language-${lang.code}-menu`}
          >
            <span className="mr-1">{lang.flag}</span>
            {lang.name}
          </Button>
        ))}
      </div>
    );
  }

  // Icon variant - default dropdown trigger
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          className={`relative ${className}`}
          data-testid="language-toggle"
          aria-label={t(translations.language)}
        >
          <motion.span
            key={languagePreference}
            initial={{ scale: 0.5, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ duration: 0.2 }}
            className="text-lg"
          >
            {getCurrentFlag()}
          </motion.span>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent 
        align="end" 
        className="w-[calc(100vw-32px)] sm:w-44 max-w-[400px] mx-4 sm:mx-0"
        sideOffset={8}
      >
        <DropdownMenuItem 
          onClick={() => setLanguage('system')}
          className="flex items-center justify-between cursor-pointer"
          data-testid="language-system"
        >
          <div className="flex items-center gap-2">
            <Globe className="w-4 h-4" />
            <span>{t(translations.system)}</span>
          </div>
          {languagePreference === 'system' && <Check className="w-4 h-4 text-orange-500" />}
        </DropdownMenuItem>
        {Object.values(SUPPORTED_LANGUAGES).map((lang) => (
          <DropdownMenuItem 
            key={lang.code}
            onClick={() => setLanguage(lang.code)}
            className="flex items-center justify-between cursor-pointer"
            data-testid={`language-${lang.code}`}
          >
            <div className="flex items-center gap-2">
              <span className="text-base">{lang.flag}</span>
              <span>{lang.name}</span>
            </div>
            {languagePreference === lang.code && <Check className="w-4 h-4 text-orange-500" />}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export default LanguageToggle;
